import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Alert,
  Fab,
  Paper,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Refresh,
  ArrowForward,
  Edit as EditIcon,
  Check,
} from '@mui/icons-material';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '../stores/auth-store';
import { useAccountsStore } from '../stores/accounts-store';
import { accountsService } from '../services/accounts-service';
import { investmentsService, ConsolidatedHolding } from '../services/investments-service';
import { dashboardService, DashboardSummary, DashboardLink } from '../services/dashboard-service';
import { NetWorthD3Chart } from '../components/NetWorthD3Chart';
import {
  AccountsOverviewCard,
  AccountsOverviewFilters,
} from '../components/AccountsOverviewCard';
import { PortfolioSummaryCard } from '../components/PortfolioSummaryCard';
import { DashboardSections } from '../components/dashboard/DashboardSections';
import { PlanCard } from '../components/dashboard/PlanCard';
import { RetirementReadinessCard } from '../components/dashboard/RetirementReadinessCard';
import { TimelineCard } from '../components/dashboard/TimelineCard';
import { investmentPlanService, type AllocationResponse } from '../services/investment-plan-service';
import { MainLayout } from '../components/MainLayout';
import { OnboardingWizard } from '../components/OnboardingWizard';
import {
  DashboardLayoutService,
  DashboardLayoutConfig,
} from '../services/dashboard-layout-service';
import {
  DashboardEditPanel,
  WIDGET_CATALOG,
  DEFAULT_ORDER,
  buildDefaultConfig,
  WidgetDefinition,
} from '../components/DashboardEditPanel';

interface DashboardPageProps {
  onLogout?: () => void;
}

/**
 * The home dashboard (issue #27).
 *
 * Top to bottom in the order a household acts on it: what needs doing, where
 * it stands, this month's bills, where the money went, the latest analysis,
 * cash flow and recent activity -- all from one GET /dashboard/summary -- then
 * the customisable widgets (the net-worth trend and the accounts overview).
 *
 * The summary and the accounts load independently: a failed summary leaves the
 * accounts usable and says so, rather than blanking the page.
 */
export const DashboardPage: React.FC<DashboardPageProps> = () => {
  const navigate = useNavigate();
  const {
    accounts,
    isLoading: accountsLoading,
    error: accountsError,
    setAccounts,
    setLoading: setAccountsLoading,
    setError: setAccountsError,
  } = useAccountsStore();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [plan, setPlan] = useState<AllocationResponse | null>(null);

  const [portfolioHoldings, setPortfolioHoldings] = useState<ConsolidatedHolding[]>([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);

  // --- Layout state ---
  const [isEditMode, setIsEditMode] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState<DashboardLayoutConfig>(buildDefaultConfig());
  const [, setLayoutLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(
    () => localStorage.getItem('onboarding_completed') !== 'true'
  );

  const widgetMap = useMemo(() => {
    const map = new Map<string, WidgetDefinition>();
    WIDGET_CATALOG.forEach((w) => map.set(w.id, w));
    return map;
  }, []);

  /** Loads the summary sections, and the investment plan card (issue #31). */
  const loadSummary = async () => {
    setSummaryLoading(true);
    // Independent and best-effort: a plan error never blanks the dashboard.
    investmentPlanService.allocation().then(setPlan).catch(() => setPlan(null));
    try {
      setSummary(await dashboardService.getSummary());
      setSummaryError(null);
    } catch (error: any) {
      setSummaryError(error?.message || 'Failed to load the dashboard summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  /** Loads the accounts (and the optional portfolio widget's holdings). */
  const loadAccounts = async () => {
    try {
      setAccountsLoading(true);
      setPortfolioLoading(true);
      setAccountsError(null);
      const accountsResponse = await accountsService.getAccountsWithBalances();
      setAccounts(accountsResponse.data || []);
      try {
        const portfolioResponse = await investmentsService.getConsolidatedHoldings();
        setPortfolioHoldings(portfolioResponse.data?.holdings || []);
      } catch (err) {
        console.error('Failed to load portfolio:', err);
      }
    } catch (error: any) {
      console.error('Failed to load accounts:', error);
      setAccountsError(error.message || 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
      setPortfolioLoading(false);
    }
  };

  const loadDashboardData = () => {
    loadSummary();
    loadAccounts();
  };

  /** Loads the saved dashboard layout from the backend. */
  const loadLayout = useCallback(async () => {
    try {
      setLayoutLoading(true);
      const response = await DashboardLayoutService.getLayout();
      const parsed = DashboardLayoutService.parseLayout(response.layout);
      if (parsed) {
        // Reason: a saved layout may name widgets that no longer exist (the
        // old Net Worth Card / Quick Stats / Recent Activity, superseded by
        // the summary sections) and miss ones added since. Unknown ids are
        // dropped; new ones are appended to the order.
        const knownIds = new Set(WIDGET_CATALOG.map((w) => w.id));
        const validOrder = parsed.order.filter((id) => knownIds.has(id));
        const missingFromOrder = DEFAULT_ORDER.filter((id) => !validOrder.includes(id));
        const validVisible = parsed.visibleWidgets.filter((id) => knownIds.has(id));

        setLayoutConfig({
          visibleWidgets: validVisible.length > 0 ? validVisible : buildDefaultConfig().visibleWidgets,
          order: [...validOrder, ...missingFromOrder],
        });
      }
    } catch {
      // Reason: If the backend call fails (e.g. first load, 404), fall back to defaults.
      setLayoutConfig(buildDefaultConfig());
    } finally {
      setLayoutLoading(false);
    }
  }, []);

  useEffect(() => {
    if (useAuthStore.getState().token) {
      loadDashboardData();
      loadLayout();
    }
  }, [loadLayout]);

  const handleToggleEditMode = async () => {
    if (isEditMode) {
      try {
        const serialized = DashboardLayoutService.serializeLayout(layoutConfig);
        await DashboardLayoutService.saveLayout(serialized);
        setSnackbarMessage('Dashboard layout saved');
      } catch {
        setSnackbarMessage('Failed to save layout');
      }
    }
    setIsEditMode((prev) => !prev);
  };

  const handleResetLayout = async () => {
    try {
      const response = await DashboardLayoutService.resetLayout();
      const parsed = DashboardLayoutService.parseLayout(response.layout);
      const knownIds = new Set(WIDGET_CATALOG.map((w) => w.id));
      setLayoutConfig(
        parsed && parsed.visibleWidgets.some((id) => knownIds.has(id)) ? parsed : buildDefaultConfig(),
      );
      setSnackbarMessage('Dashboard reset to default');
    } catch {
      setLayoutConfig(buildDefaultConfig());
      setSnackbarMessage('Dashboard reset to default (locally)');
    }
  };

  const handleToggleWidget = (widgetId: string) => {
    setLayoutConfig((prev) => {
      const isVisible = prev.visibleWidgets.includes(widgetId);
      return {
        ...prev,
        visibleWidgets: isVisible
          ? prev.visibleWidgets.filter((id) => id !== widgetId)
          : [...prev.visibleWidgets, widgetId],
      };
    });
  };

  const handleMoveWidget = (widgetId: string, direction: 'up' | 'down') => {
    setLayoutConfig((prev) => {
      const idx = prev.order.indexOf(widgetId);
      if (idx === -1) return prev;
      const newOrder = [...prev.order];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= newOrder.length) return prev;
      [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
      return { ...prev, order: newOrder };
    });
  };

  const isLoading = accountsLoading;

  /** Freshness keyed by account id, for the accounts overview. */
  const freshness = useMemo(
    () => Object.fromEntries((summary?.account_freshness ?? []).map((f) => [f.account_id, f])),
    [summary],
  );

  /** Maps a section's link onto the router. The backend picks the target. */
  const handleNavigate = useCallback(
    (link: DashboardLink) => {
      navigate({ to: link.to as any, params: link.params as any, search: link.search as any });
    },
    [navigate],
  );

  const handleAddAccount = () => {
    navigate({ to: '/accounts' });
  };

  const handleViewAccount = (account: any) => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  /**
   * "View all N accounts" on the overview card: carries the card's type and
   * institution filters across in the URL.
   */
  const handleViewAllAccounts = (filters: AccountsOverviewFilters) => {
    navigate({
      to: '/accounts',
      search: {
        type: filters.typeFilter === 'All' ? undefined : filters.typeFilter,
        institution: filters.institutionQuery.trim() || undefined,
      },
    });
  };

  /** Renders the appropriate widget component given its catalog ID. */
  const renderWidget = (widgetId: string): React.ReactNode => {
    switch (widgetId) {
      case 'net-worth-chart':
        return <NetWorthD3Chart />;
      case 'portfolio-summary':
        return <PortfolioSummaryCard holdings={portfolioHoldings} isLoading={portfolioLoading} />;
      case 'accounts-overview':
        return (
          <AccountsOverviewCard
            accounts={accounts}
            isLoading={isLoading}
            onAddAccount={handleAddAccount}
            onViewAccount={handleViewAccount}
            onViewAllAccounts={handleViewAllAccounts}
            freshness={freshness}
          />
        );
      default:
        return null;
    }
  };

  /** Handles onboarding completion or skip: hides wizard and reloads data. */
  const handleOnboardingDone = () => {
    setShowOnboarding(false);
    loadDashboardData();
  };

  const visibleWidgetsInOrder = useMemo(() => {
    return layoutConfig.order.filter((id) => layoutConfig.visibleWidgets.includes(id));
  }, [layoutConfig]);

  // Show the onboarding wizard only when we KNOW the household has no
  // accounts: `accounts` is also empty when the fetch FAILED, and greeting a
  // household of forty accounts with "add your first account" reads as "your
  // data is gone".
  const loadFailed = Boolean(accountsError);
  if (!isLoading && !loadFailed && accounts.length === 0 && showOnboarding) {
    return (
      <MainLayout>
        <OnboardingWizard onComplete={handleOnboardingDone} onSkip={handleOnboardingDone} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {(accountsError || summaryError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {accountsError || summaryError}
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3, gap: 2, flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            What needs doing, where the money went, and where you stand
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={isEditMode ? 'Save & finish editing' : 'Customize widgets'}>
            <Button
              startIcon={isEditMode ? <Check /> : <EditIcon />}
              onClick={handleToggleEditMode}
              variant={isEditMode ? 'contained' : 'outlined'}
              size="small"
              color={isEditMode ? 'success' : 'primary'}
            >
              {isEditMode ? 'Done Editing' : 'Edit Dashboard'}
            </Button>
          </Tooltip>
          <Button
            startIcon={<Refresh />}
            onClick={loadDashboardData}
            disabled={isLoading || summaryLoading}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* The summary sections, top to bottom in the order a household acts on them. */}
      <DashboardSections summary={summary} isLoading={summaryLoading} onNavigate={handleNavigate} />
      {plan && (
        <Box sx={{ mt: 3 }}>
          <PlanCard data={plan} onNavigate={handleNavigate} />
        </Box>
      )}
      {/* Household timeline (issue #39): next five and last three events. */}
      <Box sx={{ mt: 3, '&:empty': { display: 'none' } }}>
        <TimelineCard onNavigate={handleNavigate} />
      </Box>
      {/* Household retirement readiness (issue #32): renders only once opted in. */}
      <Box sx={{ mt: 3, '&:empty': { display: 'none' } }}>
        <RetirementReadinessCard onNavigate={handleNavigate} />
      </Box>

      {isEditMode && (
        <Box sx={{ mt: 3 }}>
          <DashboardEditPanel
            layoutConfig={layoutConfig}
            widgetMap={widgetMap}
            onToggleWidget={handleToggleWidget}
            onMoveWidget={handleMoveWidget}
            onReset={handleResetLayout}
          />
        </Box>
      )}

      {/* Customisable widgets below the summary. */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        {visibleWidgetsInOrder.map((widgetId) => {
          const def = widgetMap.get(widgetId);
          if (!def) return null;
          return (
            <Grid item xs={def.gridXs} md={def.gridMd} key={widgetId}>
              {renderWidget(widgetId)}
            </Grid>
          );
        })}
      </Grid>

      {/* Getting Started Guide - Only show if no accounts */}
      {!isLoading && accounts.length === 0 && (
        <Box sx={{ mt: 4 }}>
          <Paper
            sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}
          >
            <Typography variant="h6" color="primary.main" gutterBottom>
              Getting Started with FolioWorth
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Welcome to FolioWorth!</strong> Start tracking your net worth in 3 easy
              steps:
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 2 }}>
              <li>Add your financial accounts (savings, checking, investments, debts)</li>
              <li>Take regular snapshots to record current balances</li>
              <li>Track your progress with detailed reports and charts</li>
            </Box>
            <Button variant="contained" onClick={handleAddAccount} endIcon={<ArrowForward />}>
              Add Your First Account
            </Button>
          </Paper>
        </Box>
      )}

      {/* Floating Action Button for Quick Add */}
      <Fab
        color="primary"
        aria-label="add account"
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
        }}
        onClick={handleAddAccount}
      >
        <Add />
      </Fab>

      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </MainLayout>
  );
};
